import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import LanguageToggle from '../components/LanguageToggle';
import { LOGO_ACCEPT, readLogoFile } from '../discovery/logo';
import { DEFAULT_WORKING_HOURS, WorkingHoursFields, workingHoursProblem } from '../discovery/workingHours';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function defaultEnd() {
  const date = new Date();
  date.setDate(date.getDate() + 14);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const fieldClass =
  'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

const DiscoveryNew = () => {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [endDate, setEndDate] = useState(defaultEnd);
  const [exclusions, setExclusions] = useState('');
  const [intent, setIntent] = useState('');
  const [language, setLanguage] = useState('en');
  const [logo, setLogo] = useState('');
  const [logoName, setLogoName] = useState('');
  const [workingHours, setWorkingHours] = useState(DEFAULT_WORKING_HOURS);
  const [pending, setPending] = useState(false);

  const pickLogo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setLogo(await readLogoFile(file));
      setLogoName(file.name);
    } catch (error) {
      toast.error(error.message || 'The logo could not be read.');
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    const hoursProblem = workingHoursProblem(workingHours);
    if (hoursProblem) {
      toast.error(hoursProblem);
      return;
    }
    setPending(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/discovery/sessions`, {
        companyName,
        endDate,
        intent,
        language,
        ...(logo ? { logo } : {}),
        exclusions: exclusions.split(',').map((item) => item.trim()).filter(Boolean),
        workingHours,
      });
      navigate(`/discovery/${response.data.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'The session could not be opened.');
      setPending(false);
    }
  };

  return (
    <AdminLayout>
      <Link to="/discovery" className="text-sm font-medium text-primary-700 hover:text-primary-800">
        All sessions
      </Link>
      <h1 className="mt-3 text-2xl font-semibold text-gray-900">Open a session</h1>
      <p className="mt-1 max-w-xl text-sm text-gray-500">
        The enrollment file from this session is what the installer uses on each customer computer.
      </p>
      <form className="mt-8 max-w-xl space-y-5" onSubmit={submit}>
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700">
            Company
          </label>
          <input id="company" className={fieldClass} value={companyName} onChange={(event) => setCompanyName(event.target.value)} required />
        </div>
        <div>
          <label htmlFor="logo" className="block text-sm font-medium text-gray-700">
            Company logo
          </label>
          <div className="mt-1 flex items-center gap-3">
            {logo ? (
              <img
                src={logo}
                alt="Company logo preview"
                className="h-12 w-auto max-w-[160px] rounded-md border border-gray-200 bg-white object-contain p-1"
              />
            ) : null}
            <label
              htmlFor="logo"
              className="inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              {logo ? 'Change logo' : 'Upload logo'}
            </label>
            {logo ? (
              <button
                type="button"
                className="text-sm font-medium text-gray-500 hover:text-red-700"
                onClick={() => {
                  setLogo('');
                  setLogoName('');
                }}
              >
                Remove
              </button>
            ) : null}
          </div>
          <input id="logo" type="file" accept={LOGO_ACCEPT} className="sr-only" onChange={pickLogo} />
          <p className="mt-1 text-sm text-gray-500">
            Optional{logoName ? ` · ${logoName}` : ''}. Shown next to the company name on this session and on the
            customer document. PNG, JPEG, WebP, GIF, or SVG, at most 512 KB.
          </p>
        </div>
        <div>
          <label htmlFor="end" className="block text-sm font-medium text-gray-700">
            Engagement end date
          </label>
          <input id="end" type="date" className={fieldClass} value={endDate} onChange={(event) => setEndDate(event.target.value)} required />
        </div>
        <div>
          <label htmlFor="intent" className="block text-sm font-medium text-gray-700">
            What the company does
          </label>
          <textarea
            id="intent"
            className={fieldClass}
            rows={3}
            value={intent}
            minLength={8}
            maxLength={600}
            onChange={(event) => setIntent(event.target.value)}
            placeholder="Wholesale distributor. The order desk takes trade orders, the warehouse picks them, and accounts invoices the same day."
            required
          />
          <p className="mt-1 text-sm text-gray-500">
            This is the lens for the summary. Describe the business, so the reading knows which work matters.
          </p>
        </div>
        <div>
          <p className="block text-sm font-medium text-gray-700">Document language</p>
          <div className="mt-2">
            <LanguageToggle value={language} onChange={setLanguage} />
          </div>
          <p className="mt-1 text-sm text-gray-500">The living document is written in this language.</p>
        </div>
        <div>
          <label htmlFor="exclusions" className="block text-sm font-medium text-gray-700">
            Excluded applications
          </label>
          <input
            id="exclusions"
            className={fieldClass}
            value={exclusions}
            placeholder="WhatsApp, Spotify"
            onChange={(event) => setExclusions(event.target.value)}
          />
          <p className="mt-1 text-sm text-gray-500">
            Optional. The observer records every application in front. Name any to leave out.
          </p>
        </div>
        <WorkingHoursFields value={workingHours} onChange={setWorkingHours} idPrefix="new-hours" />
        <button
          type="submit"
          disabled={pending}
          className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          Open session
        </button>
      </form>
    </AdminLayout>
  );
};

export default DiscoveryNew;
